import { useState, useEffect, useRef } from 'react';
import { Factory, AlertTriangle, Package, Users, TrendingUp } from 'lucide-react';
import { buildingService } from '@/services/tycoon/buildingService';
import { ProductionLineCard } from './ProductionLineCard';
import { useProductionStore } from '@/stores/productionStore';
import { showWarning, showError } from '@/utils/alertUtils';
import { GAME_FORMULAS } from '@/config/gameFormulas';
import type { OwnedBuilding } from '@/types/buildings';
import type { Product, Employee } from '@/types/tycoon';

interface FactoryViewProps {
  buildings: OwnedBuilding[];
  products: Product[];
  employees: Employee[];
  gameDate: { day: number; month: number; year: number };
  onProductionUpdate?: () => void;
}

export function FactoryView({ 
  buildings, 
  products, 
  employees,
  gameDate,
  onProductionUpdate 
}: FactoryViewProps) {
  // Store hooks
  const productionLines = useProductionStore(state => state.productionLines);
  const constraints = useProductionStore(state => state.constraints);
  const assignProductToLine = useProductionStore(state => state.assignProductToLine);
  const updateProductionLine = useProductionStore(state => state.updateProductionLine);
  const addFactory = useProductionStore(state => state.addFactory);
  const checkManufacturingConstraints = useProductionStore(state => state.checkManufacturingConstraints);
  const [selectedFactory, setSelectedFactory] = useState<string | null>(null);
  const initializedLines = useRef(new Set<string>());

  // Initialize factories and production lines in the store
  useEffect(() => {
    buildings.forEach(building => {
      const buildingType = buildingService.getBuildingType(building.buildingTypeId);
      if (buildingType?.productionLines && buildingType.productionLines > 0 && 
          !building.isUnderConstruction && !building.isUpgrading) {
        
        // Always try to add factory - the store will handle duplicates
        addFactory({
          id: building.id,
          name: buildingType.name,
          capacity: buildingType.capacity.factoryWorkers || 0,
          efficiency: 0.8, // Default efficiency
          operatingCost: buildingType.maintenanceCost || 0,
          upgrades: [],
          productionLines: buildingType.productionLines || 1,
          productionCapacity: buildingType.productionCapacity || 100,
        });

        // Initialize production lines for this factory
        for (let i = 1; i <= buildingType.productionLines; i++) {
          const lineId = `${building.id}_line_${i}`;
          
          // Only initialize if we haven't already initialized this line
          if (!initializedLines.current.has(lineId)) {
            initializedLines.current.add(lineId);
            const existingLine = productionLines.get(lineId);
            
            // Only create new line if it doesn't exist
            if (!existingLine) {
              updateProductionLine(lineId, {
                id: lineId,
                factoryBuildingId: building.id,
                lineNumber: i,
                productId: null,
                allocatedEmployees: 0,
                dailyOutput: 0,
                efficiency: 0,
                qualityScore: 0,
                status: 'idle',
              });
            }
          }
        }
      }
    });
  }, [buildings, addFactory, updateProductionLine, productionLines]);

  const handleProductAssignment = (lineId: string, productId: string | null) => {
    if (productId) {
      // Check constraints before assignment
      const manufacturingConstraints = checkManufacturingConstraints(productId, 100); // Check for 100 units
      if (!manufacturingConstraints.canProduce) {
        showWarning(manufacturingConstraints.message || 'Cannot produce this item', 'Production Constraint');
        return;
      }
    }

    // Find the product name
    const product = productId ? products.find(p => p.id === productId) : null;
    const productName = product ? product.name : '';

    // Get the line to check if it has employees allocated
    const line = productionLines.get(lineId);

    // If line has employees and a product is being assigned, calculate dailyOutput
    if (line && line.allocatedEmployees > 0 && productId) {
      const building = buildings.find(b => b.id === line.factoryBuildingId);
      const buildingType = building ? buildingService.getBuildingType(building.buildingTypeId) : null;

      if (buildingType) {
        const { efficiency, dailyOutput } = GAME_FORMULAS.production.calculateLineProductionFromBuilding(
          buildingType.productionCapacity,
          buildingType.capacity.factoryWorkers,
          buildingType.productionLines,
          line.allocatedEmployees
        );

        // Update line with product assignment and calculated output
        updateProductionLine(lineId, {
          productId,
          productName,
          status: 'active',
          dailyOutput,
          efficiency,
          lastProductChange: gameDate,
        });
        onProductionUpdate?.();
        return;
      }
    }

    // Default: just assign the product (no employees or no product)
    assignProductToLine(lineId, productId || '', productName, gameDate);
    onProductionUpdate?.();
  };

  const handleEmployeeAllocation = (lineId: string, employeeCount: number, maxCapacity: number) => {
    if (employeeCount > maxCapacity) {
      showError(`Cannot allocate more than ${maxCapacity} employees to this line`, 'Allocation Error');
      return;
    }

    // Get the line to find the factory building
    const line = productionLines.get(lineId);
    if (!line) return;

    // Get the building and building type to calculate production output
    const building = buildings.find(b => b.id === line.factoryBuildingId);
    const buildingType = building ? buildingService.getBuildingType(building.buildingTypeId) : null;

    // Calculate dailyOutput and efficiency based on employee allocation
    let dailyOutput = 0;
    let efficiency = 0;

    if (buildingType && employeeCount > 0 && line.productId) {
      const result = GAME_FORMULAS.production.calculateLineProductionFromBuilding(
        buildingType.productionCapacity,
        buildingType.capacity.factoryWorkers,
        buildingType.productionLines,
        employeeCount
      );
      efficiency = result.efficiency;
      dailyOutput = result.dailyOutput;
    }

    updateProductionLine(lineId, {
      allocatedEmployees: employeeCount,
      status: employeeCount > 0 && line.productId ? 'active' : 'idle',
      dailyOutput,
      efficiency,
    });
    onProductionUpdate?.();
  };

  const factoryBuildings = buildings.filter(b => {
    const buildingType = buildingService.getBuildingType(b.buildingTypeId);
    return buildingType?.productionLines && buildingType.productionLines > 0;
  });

  const activeProducts = products.filter(p =>
    p.status === 'manufacturing' || p.status === 'production' || p.status === 'released'
  );

  if (factoryBuildings.length === 0) {
    return (
      <div className="text-center py-12">
        <Factory className="w-16 h-16 mx-auto text-theme-text-muted mb-4" />
        <h3 className="text-lg font-medium text-theme-text-primary">No Production Facilities</h3>
        <p className="text-theme-text-muted mt-2">No buildings with production capabilities found</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Constraints Alert */}
      {constraints.length > 0 && (
        <div className="bg-theme-warning/10 border border-theme-warning/30 rounded-lg p-4">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-theme-warning mt-0.5 mr-3" />
            <div>
              <h4 className="font-medium text-theme-warning">Production Constraints</h4>
              <ul className="mt-2 space-y-1 text-sm text-theme-warning">
                {constraints.slice(0, 3).map((constraint, idx) => (
                  <li key={idx}>• {constraint.message}</li>
                ))}
                {constraints.length > 3 && (
                  <li className="text-theme-warning/70">• And {constraints.length - 3} more...</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      )}

      {/* Factory Cards */}
      <div className="space-y-6">
        {factoryBuildings.map(building => {
          const buildingType = buildingService.getBuildingType(building.buildingTypeId);
          if (!buildingType) return null;

          // Get production lines for this factory from store
          const factoryLines = Array.from(productionLines.values()).filter(
            line => line.factoryBuildingId === building.id
          );

          // Calculate factory metrics
          const totalDailyOutput = factoryLines.reduce((sum, line) => sum + line.dailyOutput, 0);
          const totalEmployees = factoryLines.reduce((sum, line) => sum + line.allocatedEmployees, 0);
          const averageEfficiency = factoryLines.length > 0
            ? Math.round(factoryLines.reduce((sum, line) => sum + line.efficiency, 0) / factoryLines.length * 100)
            : 0;
          
          // Calculate operational lines (active or paused, not idle)
          const operationalLines = factoryLines.filter(line => 
            line.status === 'active' || (line.status === 'paused' && line.productId)
          ).length;
          const totalLines = factoryLines.length;

          const isExpanded = selectedFactory === building.id;
          const isUnderConstruction = building.isUnderConstruction || building.isUpgrading;

          return (
            <div key={building.id} className="bg-theme-bg-content rounded-lg shadow-sm border border-theme-border">
              {/* Factory Header */}
              <div 
                className="p-6 cursor-pointer"
                onClick={() => setSelectedFactory(isExpanded ? null : building.id)}
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center">
                    <Factory className="w-8 h-8 text-theme-text-secondary mr-3" />
                    <div>
                      <h3 className="text-lg font-semibold text-theme-text-primary">{buildingType.name}</h3>
                      <p className="text-sm text-theme-text-muted">
                        {operationalLines}/{totalLines} lines operational
                      </p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-6">
                    <div className="text-center">
                      <div className="flex items-center text-theme-text-muted">
                        <Package className="w-5 h-5 mr-1" />
                        <span className="text-sm">Output</span>
                      </div>
                      <p className="text-xl font-semibold text-theme-text-primary">
                        {totalDailyOutput}
                        <span className="text-sm font-normal text-theme-text-muted">/day</span>
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="flex items-center text-theme-text-muted">
                        <Users className="w-5 h-5 mr-1" />
                        <span className="text-sm">Workers</span>
                      </div>
                      <p className="text-xl font-semibold text-theme-text-primary">
                        {totalEmployees}
                        <span className="text-sm font-normal text-theme-text-muted">/{buildingType.capacity.factoryWorkers}</span>
                      </p>
                    </div>
                    
                    <div className="text-center">
                      <div className="flex items-center text-theme-text-muted">
                        <TrendingUp className="w-5 h-5 mr-1" />
                        <span className="text-sm">Efficiency</span>
                      </div>
                      <p className="text-xl font-semibold text-theme-text-primary">
                        {averageEfficiency}%
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Production Lines (Expandable) */}
              {isExpanded && !isUnderConstruction && (
                <div className="border-t border-theme-border p-6 pt-0">
                  <div className="grid gap-4 mt-4">
                    {factoryLines.map(line => {
                      // Calculate available workers for this specific line
                      const totalAssemblyWorkers = employees.filter(e => e.role === 'assemblyWorker').length;
                      let allocatedElsewhere = 0;

                      // Get all valid factory building IDs to filter out stale production lines
                      const validFactoryIds = new Set(factoryBuildings.map(b => b.id));

                      // Only count allocations from production lines that belong to existing factories
                      Array.from(productionLines.values()).forEach(otherLine => {
                        if (otherLine.id !== line.id && validFactoryIds.has(otherLine.factoryBuildingId)) {
                          allocatedElsewhere += otherLine.allocatedEmployees;
                        }
                      });

                      const availableForThisLine = Math.max(0, totalAssemblyWorkers - allocatedElsewhere);
                      
                      return (
                        <ProductionLineCard
                          key={line.id}
                          line={line}
                          products={activeProducts}
                          maxEmployees={Math.floor(buildingType.capacity.factoryWorkers / buildingType.productionLines!)}
                          gameDate={gameDate}
                          onProductChange={(productId) => handleProductAssignment(line.id, productId)}
                          onEmployeeChange={(count) => handleEmployeeAllocation(
                            line.id, 
                            count, 
                            Math.floor(buildingType.capacity.factoryWorkers / buildingType.productionLines!)
                          )}
                          availableWorkers={availableForThisLine}
                          employees={employees}
                        />
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Under Construction Message */}
              {isUnderConstruction && (
                <div className="border-t border-theme-border p-6 bg-theme-bg-secondary">
                  <p className="text-center text-theme-text-muted">
                    {building.isUpgrading ? 'Factory is being upgraded...' : 'Factory is under construction...'}
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}